import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";

import { Fragment, JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Icon from "@/components/common/Icon/Icon.tsx";
import { IconCheck } from "@/components/icons";

import { ROUTES } from "@/config/routes.ts";
import { usePatchProfile } from "@/hooks/api/user.hooks";
import { useFooterLinks } from "@/hooks/ui/useFooterLinks.ts";
import { useStaffLangs } from "@/hooks/ui/useStaffLangs";
import { useIsKids } from "@/hooks/useIsKids";
import { cn } from "@/utils/cn.ts";

const HeaderMenuDisclosure = (): JSX.Element => {
  const { t } = useTranslation();

  const links = useFooterLinks();

  const isKids = useIsKids();

  const token = localStorage.getItem("splay:token");

  return (
    <div className={"flex w-full flex-col items-start gap-5 max-lg:gap-0 xl:hidden"}>
      {token && (
        <>
          <Link
            to={ROUTES.FAVORITES}
            title={t("General.my-list")}
            className={
              "responsive-transition flex w-full items-center justify-between py-4 text-[18px]/[24px] font-semibold uppercase leading-[0.875rem]"
            }
          >
            {t("General.my-list")}
          </Link>
          <hr className={"w-full border-outline"} />
        </>
      )}
      {links.map((menu) => {
        if (menu.id === 3) return;
        return (
          <Fragment key={menu.id}>
            <Disclosure>
              {({ open }) => {
                return (
                  <>
                    <DisclosureButton
                      title={open ? "Скрыть меню" : "Показать меню"}
                      className={cn(
                        "responsive-transition flex w-full items-center justify-between py-2 text-sm font-semibold uppercase leading-[0.875rem]",
                        {
                          "text-orange-1": open,
                        },
                      )}
                    >
                      <span className="text-[18px] leading-[28px]">
                        {
                          //@ts-ignore
                          t(menu.name)
                        }
                      </span>
                      {open ? (
                        <Icon name={"minus"} className={"size-3.5 stroke-on-primary"} />
                      ) : (
                        <Icon name={"plus"} className={"size-3.5 stroke-on-primary"} />
                      )}
                    </DisclosureButton>
                    <DisclosurePanel transition className={"text-gray-500"}>
                      <ul className={"w-full"}>
                        {menu.links.map((link) => {
                          return (
                            <li key={link.id}>
                              <Link
                                to={link.link}
                                //@ts-ignore
                                aria-label={t(link.name)}
                                className={"responsive-transition block w-full py-2 text-[16px] leading-6 text-white"}
                              >
                                {
                                  //@ts-ignore
                                  t(link.name)
                                }
                              </Link>
                            </li>
                          );
                        })}
                        {menu.links && menu.id === 2 ? (
                          <li>
                            <Link
                              to={ROUTES.MAIN.GENRES}
                              title={t("General.more")}
                              className={
                                "responsive-transition block py-3 uppercase leading-6 text-on-surface underline hover:text-on-primary"
                              }
                            >
                              {t("General.more")}
                            </Link>
                          </li>
                        ) : null}
                      </ul>
                    </DisclosurePanel>
                  </>
                );
              }}
            </Disclosure>
            <hr className={"w-full border-outline"} />
          </Fragment>
        );
      })}
      {!isKids && (
        <Link
          to={ROUTES.SUBS}
          title={t("subscriptions")}
          className={
            "responsive-transition flex w-full items-center justify-between py-4 text-[18px]/[24px] font-semibold uppercase leading-[0.875rem]"
          }
        >
          {t("subscriptions")}
        </Link>
      )}
      <hr className={"w-full border-outline"} />

      <LanguageSwitcher />

      <hr className={"w-full border-outline"} />
      {!isKids && (
        <>
          <Link
            to={ROUTES.STAFF.USER_SETTINGS}
            title={t("settings")}
            className={
              "responsive-transition flex w-full items-center justify-between py-4 text-[18px]/[24px] font-semibold uppercase leading-[0.875rem]"
            }
          >
            {t("settings")}
          </Link>
          <hr className={"w-full border-outline"} />
        </>
      )}
    </div>
  );
};

export default HeaderMenuDisclosure;

const LanguageSwitcher = () => {
  const langs = useStaffLangs();
  const { t, i18n } = useTranslation();
  const profileId = localStorage.getItem("splay:profileId");
  const { mutate } = usePatchProfile();
  const updateLanguage = (lang: string) => {
    i18n.changeLanguage(lang);

    if (profileId) {
      mutate({
        user_id: +profileId,
        //@ts-ignore
        data: {
          interface_lang: lang,
        },
      });
    }
  };

  return (
    <>
      <Disclosure>
        {({ open }) => (
          <>
            <DisclosureButton
              title={open ? "Скрыть меню" : "Показать меню"}
              className={cn(
                "responsive-transition flex w-full items-center justify-between py-2 text-sm font-semibold uppercase leading-[0.875rem]",
                {
                  "text-orange-1": open,
                },
              )}
            >
              <span className="text-[18px] leading-[28px]">{t("language")}</span>
              {open ? (
                <Icon name={"minus"} className={"size-3.5 stroke-on-primary"} />
              ) : (
                <Icon name={"plus"} className={"size-3.5 stroke-on-primary"} />
              )}
            </DisclosureButton>

            <DisclosurePanel transition className={"w-full"}>
              <ul className={"w-full"}>
                {langs.map((lang) => (
                  <button
                    className={cn("flex w-full items-center justify-between py-2", {
                      "text-orange-1": i18n.language === lang.value,
                    })}
                    onClick={() => updateLanguage(lang.value)}
                  >
                    <span className="flex items-center gap-3">
                      <img src={lang.image} alt={lang.title} /> {lang.title}
                    </span>
                    {i18n.language === lang.value && (
                      <span>
                        <IconCheck
                          className={cn("size-6 stroke-on-primary", {
                            "stroke-orange-1": i18n.language === lang.value,
                          })}
                        />
                      </span>
                    )}
                  </button>
                ))}
              </ul>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </>
  );
};
